import { useQuery } from '@tanstack/react-query';
import { getApiBaseUrl } from '../lib/apiBase';

export interface GitHubInstallation {
  id: string;
  installationId: number;
  accountLogin: string;
  accountType: string;
  status: string;
  lastSyncAt: string | null;
  createdAt: string;
}

export interface GitHubRepository {
  id: number;
  name: string;
  fullName: string;
  owner: string;
  isPrivate: boolean;
  defaultBranch: string;
}

async function fetchGitHubInstallations(): Promise<GitHubInstallation[]> {
  const response = await fetch(`${getApiBaseUrl()}/api/v1/github/installations`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch GitHub installations');
  }

  return response.json();
}

async function fetchInstallationRepositories(installationId: number): Promise<GitHubRepository[]> {
  const response = await fetch(`${getApiBaseUrl()}/api/v1/github/installations/${installationId}/repositories`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch installation repositories');
  }

  return response.json();
}

async function fetchGitHubInstallUrl(): Promise<{ url: string }> {
  const response = await fetch(`${getApiBaseUrl()}/api/v1/github/install-url`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch install URL');
  }

  return response.json();
}

export function useGitHubInstallations() {
  return useQuery({
    queryKey: ['github-installations'],
    queryFn: fetchGitHubInstallations,
    staleTime: 60000, // 1 minute
  });
}

export function useInstallationRepositories(installationId: number | null) {
  return useQuery({
    queryKey: ['installation-repositories', installationId],
    queryFn: () => fetchInstallationRepositories(installationId!),
    enabled: !!installationId,
    staleTime: 30000, // 30 seconds
  });
}

export function useGitHubInstallUrl() {
  return useQuery({
    queryKey: ['github-install-url'],
    queryFn: fetchGitHubInstallUrl,
    staleTime: 300000, // 5 minutes
  });
}
